import React, { useEffect, useState, useMemo } from 'react';
import { FormDialog, InputField, MuiBox } from '@kis-technologies/kis-ui';
import { AreaFormData, AreaUpdateFormData, Mode } from '../../../types';
import { INVALID, VALID } from '../../../constants';
import { venueValidationSchema } from '../../../utils';
import { useKioskFormStyles } from '../styled';

interface Props {
  open: boolean;
  mode: Mode;
  initialData?: AreaUpdateFormData;
  onClose: () => void;
  onSubmit: (data: AreaFormData) => void;
}

export const AreaForm: React.FC<Props> = ({ open, mode, initialData, onClose, onSubmit }) => {
  const classes = useKioskFormStyles();
  const isEdit = mode === 'edit';

  const [formData, setFormData] = useState<AreaFormData>({
    name: '',
    description: ''
  });
  const [validation, setValidation] = useState<{ [x: string]: string }>({});
  const [validationMessage, setValidationMessage] = useState<{ [x: string]: string | null }>({});

  /**
   * PREFILL DATA IN EDIT MODE
   */
  useEffect(() => {
    if (isEdit && initialData) {
      setFormData({
        name: initialData.name || '',
        description: initialData.description || ''
      });
    }
  }, [initialData, isEdit]);

  /**
   * INPUT CHANGE
   */
  const updateField = (key: keyof AreaFormData, value: string) => {
    if (validation[key] === INVALID) {
      setValidation({ ...validation, [key]: VALID });
      setValidationMessage({ ...validationMessage, [key]: null });
    }

    setFormData({
      ...formData,
      [key]: value
    });
  };
  /**
   * INPUT BLUR VALIDATION
   */
  const onInputBlur = async (
    key: keyof AreaFormData,
    e: React.ChangeEvent<HTMLInputElement>,
    value: string | null = null
  ) => {
    e.preventDefault();

    const trimmed = e.target.value.trim();

    setFormData({
      ...formData,
      [key]: trimmed
    });

    let invalidMessage = null;

    if (value && value.trim().length) {
      const result = await venueValidationSchema.validate({ [key]: value.trim() }).catch((err) => err);

      invalidMessage = result?.message;
    }

    setValidation({
      ...validation,
      [key]: invalidMessage ? INVALID : VALID
    });

    setValidationMessage({
      ...validationMessage,
      [key]: invalidMessage
    });
  };

  /**
   * HELPER TEXT
   */
  const validate = (key: keyof AreaFormData) => validationMessage[key];

  /**
   * DISABLE SUBMIT CONDITIONS
   */
  const hasEmptyInput = useMemo(() => !formData.name.trim() || !formData.description.trim(), [formData]);

  const hasInvalidInput = useMemo(() => Object.values(validation).includes(INVALID), [validation]);

  const buttonDisabled = hasEmptyInput || hasInvalidInput;

  /**
   * SUBMIT FORM
   */
  const handleSubmit = () => {
    const payload: AreaFormData = {
      name: formData.name.trim(),
      description: formData.description.trim()
    };

    onSubmit(payload);
  };

  return (
    <FormDialog
      open={open}
      title={isEdit ? 'Edit Area' : 'Create Area'}
      confirmButtonText={isEdit ? 'Save' : 'Create'}
      cancelButtonText="Cancel"
      cancelButtonAction={onClose}
      confirmButtonAction={handleSubmit}
      confirmButtonDisabled={buttonDisabled}
    >
      <MuiBox mt="14px">
        {/* NAME */}
        <MuiBox className={classes.formOverlayInputBox}>
          <InputField
            label="Name"
            size="small"
            sx={{ width: '524px', height: '45px' }}
            fullWidth
            value={formData.name}
            onChange={(e) => updateField('name', e.target.value)}
            onBlur={(e) => onInputBlur('name', e, e.target.value)}
            helperText={validate('name')}
            error={validation.name === INVALID}
          />
        </MuiBox>

        {/* DESCRIPTION */}
        <MuiBox className={classes.formOverlayInputBox}>
          <InputField
            label="Description"
            size="small"
            sx={{ width: '524px', height: '45px' }}
            fullWidth
            value={formData.description}
            onChange={(e) => updateField('description', e.target.value)}
            onBlur={(e) => onInputBlur('description', e, e.target.value)}
            helperText={validate('description')}
            error={validation.description === INVALID}
          />
        </MuiBox>
      </MuiBox>
    </FormDialog>
  );
};
